/**
 * useLandingMotion — the design component's own scroll behaviour, ported.
 *
 * `GardEn Landing v2.dc.html` ships a `DCLogic` class that does three things on
 * mount. All three are reproduced here with the same thresholds, because the
 * thresholds are what the section rhythm was authored against:
 *
 *   1. **Reveal.** Every `[data-reveal]` element starts at `opacity:0;
 *      translateY(46px)` and is released to its resting state once seen. The
 *      design runs BOTH an IntersectionObserver at `threshold: 0.12` and a
 *      scroll handler at `top < innerHeight * 0.9`, then re-scans at 400 /
 *      1200 / 3000 / 6000 ms to catch late-mounting nodes. The belt-and-braces
 *      is deliberate: a missed reveal leaves a whole section invisible, so the
 *      cheap redundancy stays.
 *   2. **Sticky step tracking.** The How-it-works section pins three visuals and
 *      cross-fades them against three scrolling text blocks. The active step is
 *      whichever block's CENTRE is nearest the viewport centre — not whichever
 *      is topmost, which is why a simple `IntersectionObserver` would not
 *      reproduce it.
 *   3. Cleanup of both, plus the timers.
 *
 * Reduced motion: the reveal is released immediately for everything, so the
 * page renders complete and still. Step tracking keeps working — it is
 * navigation state, not decoration.
 */
import { useEffect, useRef, useState } from "react";

const RESCAN_MS = [400, 1200, 3000, 6000] as const;

export interface LandingMotion {
  /** Attach to each of the three How-it-works text blocks, in order. */
  stepRefs: readonly [
    React.RefObject<HTMLDivElement | null>,
    React.RefObject<HTMLDivElement | null>,
    React.RefObject<HTMLDivElement | null>,
  ];
  /** 0 | 1 | 2 — the step whose centre is nearest the viewport centre. */
  activeStep: number;
}

export function useLandingMotion(): LandingMotion {
  const step0 = useRef<HTMLDivElement | null>(null);
  const step1 = useRef<HTMLDivElement | null>(null);
  const step2 = useRef<HTMLDivElement | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const release = (el: HTMLElement): void => {
      el.dataset["revealed"] = "1";
      el.style.opacity = "1";
      el.style.transform = "none";
    };

    if (reduced) {
      document
        .querySelectorAll<HTMLElement>("[data-reveal]")
        .forEach(release);
    }

    const steps = [step0, step1, step2];
    let current = 0;

    const onScroll = (): void => {
      if (!reduced) {
        document
          .querySelectorAll<HTMLElement>("[data-reveal]")
          .forEach((el) => {
            if (el.dataset["revealed"]) return;
            const r = el.getBoundingClientRect();
            if (r.top < window.innerHeight * 0.9 && r.bottom > 0) release(el);
          });
      }

      const centre = window.innerHeight / 2;
      let best = 0;
      let bd = 1e9;
      steps.forEach((ref, i) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - centre);
        if (d < bd) {
          bd = d;
          best = i;
        }
      });
      if (best !== current) {
        current = best;
        setActiveStep(best);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const timers: number[] = [];
    let io: IntersectionObserver | undefined;

    if (!reduced && typeof IntersectionObserver === "function") {
      const seen = new WeakSet<Element>();
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              release(en.target as HTMLElement);
              io?.unobserve(en.target);
            }
          });
        },
        { threshold: 0.12 },
      );
      const scan = (): void => {
        document.querySelectorAll("[data-reveal]").forEach((el) => {
          if (seen.has(el)) return;
          seen.add(el);
          io?.observe(el);
        });
      };
      scan();
      RESCAN_MS.forEach((ms) => timers.push(window.setTimeout(scan, ms)));
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      timers.forEach((id) => window.clearTimeout(id));
      io?.disconnect();
    };
  }, []);

  return { stepRefs: [step0, step1, step2], activeStep };
}
